/* ------------------------------------------------------------------
   FollowerChart.js — Analytics › 'Follower growth' (Part E, T29).
   One blue line (Main.dc.html), a hover / arrow-key tooltip, and a
   'Show as table' switch so screen-reader and keyboard users get every
   number. series = [{ date: 'YYYY-MM-DD', followers: number|null }].
------------------------------------------------------------------ */
import React, { useState } from 'react'

const W = 640
const H = 230
const LEFT = 44
const RIGHT = 630
const TOP = 20
const BOTTOM = 200
const BLUE = '#2563eb'

export const shortDate = day =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
const full = n => (n === null || n === undefined ? '—' : Math.round(n).toLocaleString('en-US'))

/* Round gridline values (3–6 lines) from a whole-number 'nice' step that covers min…max. */
export function ticks (min, max) {
  if (min === max) { min -= 1; max += 1 }
  const raw = (max - min) / 4
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = Math.max(1, [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw))
  const out = []
  for (let v = Math.floor(min / step) * step; out.length < 2 || out[out.length - 1] < max; v += step) out.push(v)
  return out
}

export default function FollowerChart ({ series = [], rangeLabel }) {
  const [asTable, setAsTable] = useState(false)
  const [active, setActive] = useState(null)

  const known = series.map((p, i) => ({ ...p, i })).filter(p => p.followers !== null && p.followers !== undefined)
  const geo = (() => {
    if (!known.length) return null
    const vals = known.map(p => p.followers)
    const t = ticks(Math.min(...vals), Math.max(...vals))
    const lo = t[0]
    const hi = t[t.length - 1]
    const x = i => LEFT + (series.length > 1 ? (i / (series.length - 1)) * (RIGHT - LEFT) : (RIGHT - LEFT) / 2)
    const y = v => BOTTOM - ((v - lo) / (hi - lo || 1)) * (BOTTOM - TOP)
    return { t, x, y }
  })()

  const first = known[0]
  const last = known[known.length - 1]
  const summary = first && last
    ? (first === last
      ? `${full(last.followers)} followers on ${shortDate(last.date)}`
      : `Followers ${last.followers >= first.followers ? 'rose' : 'fell'} from ${full(first.followers)} to ${full(last.followers)} over ${series.length} days`)
    : 'No follower numbers yet'

  const pick = clientX => (e) => {
    if (!geo || !known.length) return
    const box = e.currentTarget.getBoundingClientRect()
    const vx = ((clientX(e) - box.left) / (box.width || W)) * W
    let best = known[0]
    for (const p of known) if (Math.abs(geo.x(p.i) - vx) < Math.abs(geo.x(best.i) - vx)) best = p
    setActive(best.i)
  }
  const onKey = e => {
    if (!known.length) return
    const pos = known.findIndex(p => p.i === active)
    if (e.key === 'ArrowRight') { e.preventDefault(); setActive(known[Math.min(pos < 0 ? known.length - 1 : pos + 1, known.length - 1)].i) }
    if (e.key === 'ArrowLeft') { e.preventDefault(); setActive(known[Math.max(pos < 0 ? known.length - 1 : pos - 1, 0)].i) }
    if (e.key === 'Escape') setActive(null)
  }

  const hover = active !== null && geo ? series[active] : null
  const tipW = 118
  const tipX = hover ? Math.min(Math.max(geo.x(active) - tipW - 8, LEFT), RIGHT - tipW) : 0
  const midIdx = Math.floor((series.length - 1) / 2)

  return (
    <figure style={{ gridColumn: 'span 3', margin: 0, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, minWidth: 0 }} className='an-chart'>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div>
          <figcaption style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Follower growth</figcaption>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>{rangeLabel}</div>
        </div>
        <button
          type='button'
          aria-pressed={asTable}
          onClick={() => setAsTable(v => !v)}
          style={{ padding: '5px 10px', border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, font: '600 12px inherit', cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {asTable ? 'Show as chart' : 'Show as table'}
        </button>
      </div>

      {asTable ? (
        <div style={{ maxHeight: 230, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <caption className='visually-hidden'>Followers per day, {rangeLabel}</caption>
            <thead>
              <tr>
                <th scope='col' style={{ textAlign: 'left', color: '#6b7280', fontWeight: 600, padding: '4px 0' }}>Date</th>
                <th scope='col' style={{ textAlign: 'right', color: '#6b7280', fontWeight: 600, padding: '4px 0' }}>Followers</th>
              </tr>
            </thead>
            <tbody>
              {series.map(p => (
                <tr key={p.date} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '4px 0' }}>{shortDate(p.date)}</td>
                  <td style={{ padding: '4px 0', textAlign: 'right' }}>{full(p.followers)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <svg
          width='100%' height='230' viewBox={`0 0 ${W} ${H}`} role='img' aria-label={summary}
          tabIndex={0} data-testid='follower-chart'
          style={{ display: 'block', outline: 'none' }}
          onMouseMove={pick(e => e.clientX)}
          onMouseLeave={() => setActive(null)}
          onTouchStart={pick(e => e.touches[0].clientX)}
          onFocus={() => { if (active === null && last) setActive(last.i) }}
          onBlur={() => setActive(null)}
          onKeyDown={onKey}
        >
          {geo ? (
            <>
              <g stroke='#eef2f7' strokeWidth='1'>
                {geo.t.map(v => <line key={v} x1={LEFT} y1={geo.y(v)} x2={RIGHT} y2={geo.y(v)} />)}
              </g>
              <g fill='#6b7280' fontSize='11' fontFamily='system-ui, sans-serif'>
                {geo.t.map(v => <text key={v} x='4' y={geo.y(v) + 4}>{full(v)}</text>)}
                {series.length > 0 && <text x={LEFT} y='222'>{shortDate(series[0].date)}</text>}
                {series.length > 2 && <text x={geo.x(midIdx)} y='222' textAnchor='middle'>{shortDate(series[midIdx].date)}</text>}
                {series.length > 1 && <text x={RIGHT} y='222' textAnchor='end'>{shortDate(series[series.length - 1].date)}</text>}
              </g>
              <polyline
                fill='none' stroke={BLUE} strokeWidth='2' strokeLinejoin='round' strokeLinecap='round'
                points={known.map(p => `${geo.x(p.i).toFixed(1)},${geo.y(p.followers).toFixed(1)}`).join(' ')}
              />
              {hover && (
                <g data-testid='chart-tooltip'>
                  <line x1={geo.x(active)} y1={TOP} x2={geo.x(active)} y2={BOTTOM} stroke='#94a3b8' strokeWidth='1' strokeDasharray='3 3' />
                  <circle cx={geo.x(active)} cy={geo.y(hover.followers)} r='5' fill={BLUE} stroke='#ffffff' strokeWidth='2' />
                  <rect x={tipX} y='30' width={tipW} height='38' rx='6' fill='#111827' />
                  <text x={tipX + 10} y='46' fill='#ffffff' fontSize='11' fontFamily='system-ui, sans-serif'>{shortDate(hover.date)}</text>
                  <text x={tipX + 10} y='61' fill='#ffffff' fontSize='12' fontWeight='700' fontFamily='system-ui, sans-serif'>{full(hover.followers)} followers</text>
                </g>
              )}
            </>
          ) : (
            <text x={W / 2} y={H / 2} textAnchor='middle' fill='#6b7280' fontSize='13' fontFamily='system-ui, sans-serif'>
              No follower numbers yet
            </text>
          )}
        </svg>
      )}
      <div aria-live='polite' className='visually-hidden'>
        {hover ? `${shortDate(hover.date)}: ${full(hover.followers)} followers` : ''}
      </div>
    </figure>
  )
}
